// Painel de UI do rito colegiado (ADR-0014 — IA é apoio, decisão humana).
// AUTO-CONTIDO. Rito colegiado é o ÚNICO (ADR-0014, F3) → sempre renderiza, sem
// gate de capability. Cobre os estados: voto cego (X/N, só contagem), fechar
// quórum → avançar à decisão, o comitê DECIDE ou REABRE (comite-reabre),
// REABERTURA (definir retorno) e decisão APLICADA.
//
// Voto cego: este painel NUNCA mostra quem votou nem o teor — só o quorumProgress
// (contagem) que o backend deriva. Os pareceres em si (revelados só no fechamento do
// quórum pelo backend) continuam na aba "Pareceres".
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DenunciaState } from '@aions/core';
import { apiFetch } from '../lib/auth-client';
import { Banner, Button, Card, Badge, StatusDot, Field, Textarea } from '@aions/ui-primitives';

interface Props {
  caseId: string;
  state: DenunciaState;
  quorumProgress: { voted: number; total: number } | null;
  /**
   * Id do membro logado — INJETADO, não lido de contexto. O painel recebe todo o
   * resto por prop; puxar `useSession` aqui acoplaria um componente de
   * apresentação ao contexto e obrigaria todo teste dele a montar Provider.
   * É a cura que o CLAUDE.md prescreve para acoplamento: injetar a dependência,
   * não contornar o mock.
   */
  meuId: string;
  /**
   * Quem ainda NÃO emitiu parecer, já resolvido para rótulo legível pelo pai
   * (decisão do operador 11/08). Injetado, como o `meuId`.
   *
   * 🔴 O que esta lista é e o que NÃO é: são as pessoas que ainda não AGIRAM. O
   * TEOR do voto de ninguém aparece aqui nem em lugar nenhum antes do quórum
   * fechar — o servidor continua redigindo parecer alheio
   * (`redactPareceresBlindVote`). Sigilo de COMO cada um votou: intacto.
   */
  pendentes: ReadonlyArray<{ id: string; label: string }>;
  /** Recarrega o caso após uma ação que muta o estado. */
  onChanged: () => void;
}

export function RitoV2Panel({ caseId, state, quorumProgress, meuId, pendentes, onChanged }: Props) {
  const { t } = useTranslation();
  // `pareceres` só existe nos kinds que têm voto; `in` evita cast e mantém o
  // acesso seguro sob a união discriminada.
  const pareceresDoRoster: ReadonlyArray<{ memberId: string }> =
    'pareceres' in state ? (state.pareceres as ReadonlyArray<{ memberId: string }>) : [];
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [apontamentos, setApontamentos] = useState('');

  async function act(path: string, body?: unknown): Promise<unknown | null> {
    setBusy(true);
    setError(null);
    try {
      const resp = await apiFetch(`/api/comite/cases/${caseId}/${path}`, {
        method: 'POST',
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
      const json: unknown = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError((json as { error?: string }).error ?? `HTTP ${String(resp.status)}`);
        return null;
      }
      return json;
    } catch {
      setError('network');
      return null;
    } finally {
      setBusy(false);
    }
  }

  const kind = state.kind;

  // ---- AvaliacaoEmCurso — voto cego + fechar quórum (avançar à decisão) ------
  if (kind === 'AvaliacaoEmCurso' && quorumProgress) {
    const completo = quorumProgress.voted >= quorumProgress.total;
    return (
      <Card>
        <div role="status" aria-live="polite">
          <StatusDot tone={completo ? 'success' : 'live'} live={!completo} />{' '}
          <strong>
            {t('rito_v2.quorum_progress', {
              voted: quorumProgress.voted,
              total: quorumProgress.total,
            })}
          </strong>
          <p>{completo ? t('rito_v2.quorum_completo') : t('rito_v2.quorum_voto_cego')}</p>
        </div>

        {/* O QUE FALTA A VOCÊ FAZER — a pergunta que o X/N não responde.
            A contagem diz quantos faltam no colegiado; não diz se um dos que
            faltam é você, e era isso que o membro precisava saber ao abrir o caso.

            🔴 Deriva do PRÓPRIO parecer, e só dele. `redactPareceresBlindVote`
            (comite/index.ts) entrega ao membro apenas `p.memberId === viewerId`
            enquanto o quórum está aberto, então este teste é exato nos dois regimes
            — redigido (quórum aberto) e completo (pós-fechamento, quando o histórico
            abre por desenho).

            ⚠️ O que NÃO entra aqui, por invariante: QUEM ainda não votou. Nomear os
            faltantes revelaria por complemento quem já votou e, somado ao placar,
            estreita quem votou como — é o voto cego, e o comentário do
            `blindVotePhase` registra que uma superfície já o furou em produção. O
            payload nem carrega esses ids: não é omissão de UI, é ausência de dado. */}
        {(() => {
          const euJaVotei = pareceresDoRoster.some((p) => p.memberId === meuId);
          return (
            <p
              className={`meu-parecer meu-parecer--${euJaVotei ? 'feito' : 'pendente'}`}
              data-testid="meu-parecer-status"
            >
              {euJaVotei
                ? t('rito_v2.meu_parecer_feito', {
                    defaultValue: 'Seu parecer já foi emitido e está selado até o quórum fechar.',
                  })
                : t('rito_v2.meu_parecer_pendente', {
                    defaultValue: 'Você ainda não emitiu seu parecer neste caso.',
                  })}
            </p>
          );
        })()}
        {pendentes.length > 0 && (
          <div className="parecer-pendentes" data-testid="parecer-pendentes">
            <strong>
              {t('rito_v2.pendentes_label', {
                count: pendentes.length,
                defaultValue: 'Ainda não emitiram parecer ({{count}}):',
              })}
            </strong>
            <ul>
              {pendentes.map((m) => (
                // `title={id}` porque o rótulo pode SER o id quando o diretório não
                // carregou — nunca omitir a linha (senão a lista deixa de bater com
                // o X/N do quórum).
                <li key={m.id} title={m.id}>
                  {m.label}
                </li>
              ))}
            </ul>
          </div>
        )}
        {completo && (
          <Button
            onClick={() => {
              void act('fechar-quorum').then((r) => {
                if (r) onChanged();
              });
            }}
            disabled={busy}
          >
            {t('rito_v2.avancar_decisao')}
          </Button>
        )}
        {error && (
          <Banner tone="coral" role="alert">
            {t('rito_v2.erro', { error })}
          </Banner>
        )}
      </Card>
    );
  }

  // ---- AguardandoDecisao — quórum fechado: o comitê DECIDE (aba de decisão) ou
  //      REABRE a instrução (ADR-0014 — decisão e reabertura são humanas). ------
  if (kind === 'AguardandoDecisao') {
    return (
      <Card>
        <strong>{t('rito_v2.decisao_title')}</strong>
        <p>{t('rito_v2.decisao_instrucao')}</p>
        <Field label={t('rito_v2.reabrir_apontamentos')}>
          {({ id, describedBy, invalid }) => (
            <Textarea
              id={id}
              aria-describedby={describedBy}
              invalid={invalid}
              value={apontamentos}
              onChange={(e) => {
                setApontamentos(e.target.value);
              }}
              rows={2}
            />
          )}
        </Field>
        <Button
          variant="ghost"
          onClick={() => {
            void act('comite-reabre', { apontamentos }).then((r) => {
              if (r) onChanged();
            });
          }}
          disabled={busy || apontamentos.trim().length === 0}
        >
          {t('rito_v2.reabrir')}
        </Button>
        {error && (
          <Banner tone="coral" role="alert">
            {t('rito_v2.erro', { error })}
          </Banner>
        )}
      </Card>
    );
  }

  // ---- AguardandoRetornoComite — reabertura (apontamentos + definir retorno) --
  if (kind === 'AguardandoRetornoComite') {
    return (
      <Card>
        <div role="status" aria-live="polite">
          <Badge tone="amber">
            {t('rito_v2.reabertura_count', { count: state.reaberturasCount })}
          </Badge>
          <strong>{t('rito_v2.reabertura_title')}</strong>
          <p>
            <em>{t('rito_v2.reabertura_apontamentos')}:</em> {state.apontamentos}
          </p>
        </div>
        <div>
          <Button
            onClick={() => {
              void act('definir-retorno', { target: 'instrucao' }).then((r) => {
                if (r) onChanged();
              });
            }}
            disabled={busy}
          >
            {t('rito_v2.retorno_instrucao')}
          </Button>{' '}
          <Button
            variant="ghost"
            onClick={() => {
              void act('definir-retorno', { target: 'votacao' }).then((r) => {
                if (r) onChanged();
              });
            }}
            disabled={busy}
          >
            {t('rito_v2.retorno_votacao')}
          </Button>
        </div>
        {error && (
          <Banner tone="coral" role="alert">
            {t('rito_v2.erro', { error })}
          </Banner>
        )}
      </Card>
    );
  }

  // ---- Decisao / Recursal — decisão aplicada ---------------------------------
  if (kind === 'Decisao' || kind === 'Recursal') {
    return (
      <Card>
        <Banner tone="brand" role="status">
          {t('rito_v2.aplicada_title')}: {t(`parecer.votos.${state.decisao.kind}`)}
        </Banner>
      </Card>
    );
  }

  return null;
}
